import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Tracking } from '@freelancer/tracking';
import type { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApplicationType } from './payments-tracking.types';

/**
 * Absolute sanity bound. Correlating each timer with its own deposit page visit
 * is what excludes stale timers, so this only drops values that could not be a
 * real funnel.
 */
const MAX_FUNNEL_DURATION_MS = 10 * 60 * 1000;

/** Bound the map so a user who never reaches /deposit cannot leak timers. */
const MAX_TRACKED_VISITS = 8;

/**
 * Key for a deposit page visit with no cart, such as a balance top up or a
 * legacy `refAction` flow. Only one deposit page is alive per tab, so a single
 * key is enough.
 */
const CARTLESS_VISIT_KEY = 'visit';

/** Label used when a flow builds a cart without arming the timer at a click. */
const UNATTRIBUTED_SOURCE = 'Unattributed';

/**
 * Where the timer was armed, which is also what it measures:
 *
 * - `click`: the whole funnel, from the button the user pressed.
 * - `cart`: from cart creation, for a flow that never called `start()`.
 * - `page`: from the deposit page itself, for a visit that no cart led to.
 *
 * A dashboard that reports click to payable has to filter on `click`.
 */
type ArmedAt = 'cart' | 'click' | 'page';

/**
 * What the deposit page looked like when the user left it: `error` when a
 * payments error had been shown, `loading` when the page was still working.
 * The two need different owners - `error` is a defect, `loading` is a funnel
 * hole - so the event separates them.
 */
export type AbandonedPageState = 'error' | 'loading';

interface PendingTimer {
  readonly startTime: number;
  readonly source: string;
  readonly armedAt: ArmedAt;
  /** Absent for a top up or a legacy flow, which reach the page without a cart. */
  readonly cartId?: number;
  hidden: boolean;
}

/**
 * Measures the time from a user action that leads to the deposit page
 * (accepting a milestone request, awarding a bid) until the deposit cart is
 * fully initialised and ready to pay.
 *
 * Call `start()` at the triggering click and `attach()` once the cart exists.
 * The deposit page then calls `arrive()` to get its key, and finishes with
 * either `complete()` or `abandon()`. `complete()` fires a `DepositPageReady`
 * custom event and `abandon()` fires a `DepositPageAbandoned` one, both
 * carrying the elapsed milliseconds (`value`) and the originating action
 * (`label`).
 *
 * Timers are correlated with one deposit page visit because they outlive their
 * own funnel: a user who reached the deposit page and left without paying used
 * to strand the timer, which then completed on whichever visit came next. That
 * reported the gap between two unrelated visits as a page load time.
 */
@Injectable({
  providedIn: 'root',
})
export class DepositReadyTimer {
  private tracking = inject(Tracking);
  private platformId = inject(PLATFORM_ID);

  /** Armed at the click, before the cart exists. Only `attach()` promotes it. */
  private unattached?: PendingTimer;
  private byVisit = new Map<string, PendingTimer>();
  private visibilitySubscription?: Subscription;

  start(source: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.watchVisibility();
    this.unattached = this.arm(source, 'click');
  }

  /**
   * Binds the pending timer to the cart it built, so that only the deposit page
   * for that cart can finish it. Arms a fresh timer when the flow never called
   * `start()`, since most flows that build a cart do not.
   */
  attach(cartId: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.watchVisibility();
    const armed = this.unattached ?? this.arm(UNATTRIBUTED_SOURCE, 'cart');
    this.byVisit.set(cartKey(cartId), { ...armed, cartId });
    this.unattached = undefined;
    this.prune();
  }

  /**
   * Called by the deposit page on init. Returns the key to finish with.
   *
   * Arms a timer when nothing led here with one, so that a visit which no
   * instrumented cart produced still reports. Without this, a legacy query
   * parameter flow such as `refAction=create_milestone`, or a plain balance top
   * up, is invisible: it has no cart to correlate with.
   */
  arrive(cartId: number | undefined, source: string): string {
    if (!isPlatformBrowser(this.platformId)) {
      return CARTLESS_VISIT_KEY;
    }
    this.watchVisibility();

    if (cartId === undefined) {
      // Nothing to correlate with, so always re-arm. Reusing an entry here
      // would let an earlier visit lend its start time to this one.
      this.byVisit.set(CARTLESS_VISIT_KEY, this.arm(source, 'page'));
      return CARTLESS_VISIT_KEY;
    }

    const key = cartKey(cartId);
    if (!this.byVisit.has(key)) {
      this.byVisit.set(key, { ...this.arm(source, 'page'), cartId });
      this.prune();
    }
    return key;
  }

  /** The deposit page became payable. */
  complete(key: string): void {
    const timer = this.take(key);
    if (timer === undefined) {
      return;
    }
    this.track('DepositPageReady', timer);
  }

  /** The user left the deposit page before it became payable. */
  abandon(key: string, pageState: AbandonedPageState = 'loading'): void {
    const timer = this.take(key);
    if (timer === undefined) {
      return;
    }
    this.track('DepositPageAbandoned', timer, { pageState });
  }

  /**
   * Drops the timer armed at the last `start()`, for a flow that builds a cart
   * but detours away from the deposit page. Without this the pending timer
   * outlives its funnel and the next unrelated `attach()` adopts it, which
   * charges one flow's wait to another.
   */
  discard(): void {
    this.unattached = undefined;
  }

  private arm(source: string, armedAt: ArmedAt): PendingTimer {
    return {
      startTime: window.performance.now(),
      source,
      armedAt,
      hidden: document.visibilityState === 'hidden',
    };
  }

  private take(key: string): PendingTimer | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }
    const timer = this.byVisit.get(key);
    // Delete on read so one visit cannot report twice.
    this.byVisit.delete(key);
    return timer;
  }

  private track(
    name: string,
    timer: PendingTimer,
    extra: { readonly pageState?: AbandonedPageState } = {},
  ): void {
    const elapsed = window.performance.now() - timer.startTime;
    if (elapsed > MAX_FUNNEL_DURATION_MS) {
      return;
    }
    this.tracking.trackCustomEvent(name, ApplicationType.DEPOSIT_PAGE, {
      value: elapsed.toFixed(),
      label: timer.source,
      armedAt: timer.armedAt,
      backgrounded: timer.hidden ? '1' : '0',
      ...(timer.cartId === undefined ? {} : { cartId: timer.cartId }),
      ...extra,
    });
  }

  /** Drop the oldest timers first, since a Map iterates in insertion order. */
  private prune(): void {
    while (this.byVisit.size > MAX_TRACKED_VISITS) {
      const oldest = this.byVisit.keys().next().value;
      if (oldest === undefined) {
        return;
      }
      this.byVisit.delete(oldest);
    }
  }

  private watchVisibility(): void {
    if (this.visibilitySubscription !== undefined) {
      return;
    }
    this.visibilitySubscription = fromEvent(document, 'visibilitychange')
      .pipe(filter(() => document.visibilityState === 'hidden'))
      .subscribe(() => {
        // `performance.now()` keeps counting while a tab or an app is suspended,
        // so flag the sample rather than trust its duration.
        if (this.unattached !== undefined) {
          this.unattached.hidden = true;
        }
        this.byVisit.forEach(timer => {
          timer.hidden = true;
        });
      });
  }
}

function cartKey(cartId: number): string {
  return `cart:${cartId}`;
}
